/**
 * The slash menu's decisions, as pure functions.
 *
 * Olivia, 27 Sep 2026 (design-handoffs/2026-09-27-templates-epub-slash, §4):
 * every command consumes the `/` and the filter text typed after it. On an
 * empty line the inserted block replaces that paragraph; mid-line only
 * `/query` goes and the rest of the line stays. Esc closes the menu and leaves
 * the text as typed — that is how an author gets a literal `/`.
 *
 * Kept free of React and ProseMirror so each rule is a test.
 */

/** Text a leaf node (a citation, inline math, a hard break) reads as. */
export const LEAF = "￼";

export interface SlashTrigger {
  /** What follows the `/` up to the cursor — the menu's filter. */
  query: string;
  /** Characters the trigger spans, `/` included, counted back from the cursor. */
  length: number;
}

/**
 * What the author has typed before the cursor, if it is a slash trigger.
 *
 * `/` opens the menu at the start of the line or after whitespace — so
 * `and/or`, `1/2` and `http://` stay prose. The query runs to the cursor; it
 * may hold single spaces (`/lorem 3`, `/table of contents`) but not start
 * with one (`a / b` is a sentence), and never crosses another `/` or an
 * inline node.
 */
export function parseSlashTrigger(textBefore: string): SlashTrigger | null {
  // Words separated by exactly one space, so the pattern cannot backtrack
  // exponentially over a long line that turns out not to match.
  const m = textBefore.match(/(?:^|\s)(\/((?:[^\s/￼]+(?: [^\s/￼]+)* ?)?))$/);
  if (!m) return null;
  const [, whole, query] = m;
  return { query, length: whole.length };
}

export interface SlashConsumePlan {
  /** Offsets into the line (its text content, leaves as one character each). */
  from: number;
  to: number;
  /**
   * Nothing but whitespace is left once the trigger goes: the line was empty,
   * so what the command inserts replaces the paragraph.
   */
  emptyLine: boolean;
}

/**
 * What to delete when a command runs. `from`/`to` bound the trigger in the
 * line's text. Mid-line only the trigger goes; on an otherwise empty line the
 * whole line is cleared, so a stray space does not keep the paragraph alive.
 */
export function planSlashConsume(lineText: string, from: number, to: number): SlashConsumePlan {
  const rest = lineText.slice(0, from) + lineText.slice(to);
  if (rest.trim() === "") return { from: 0, to: lineText.length, emptyLine: true };
  return { from, to, emptyLine: false };
}

/** The fields of a slash command the filter reads. */
export interface SlashMatchable {
  label: string;
  keywords: string[];
  /**
   * The command's own reading of the query, for commands that take an
   * argument: `/lorem 3` is the lorem command with a count, and "lorem 3"
   * appears in none of its keywords.
   */
  matches?: (query: string) => boolean;
}

/** Whether a command shows for the typed query. An empty query shows all. */
export function slashCommandMatches(command: SlashMatchable, query: string): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  if (command.matches?.(query.trim())) return true;
  return command.keywords.some((k) => k.toLowerCase().includes(q)) || command.label.toLowerCase().includes(q);
}
